/* PTL → PoD Analytics — shared helpers.

   Backend: proship_mongo /api/pod/analytics/* (ROLE_PROSHIPADMIN + PTL_ACCESS).
     GET summary?from&to&merchantOid&courierId        → { totals, byCourier[], byClient[], bucketLabels[], onTimeHours, trackingStartDate }
     GET shipments?…&podStatus&agingBucket&timeliness&podSource&awb&page&size → { total, page, size, rows[] }
     GET shipments.csv?… (same filters, all rows)     → csv (blob)
     GET config · PUT config {onTimeHours, agingBucketDays[], awbLookbackDays} */
import { format, startOfMonth, endOfMonth, subDays, subMonths } from 'date-fns'
import { apiConstants } from '../../common/constants'

export { downloadFile, fmtDate, fmtDateTime } from '../ptl-billing/billingUtils'

export const POD_BASE = apiConstants.POD_ANALYTICS

export const ymd = (d) => format(d, 'yyyy-MM-dd')
export const num = (n) => (n === null || n === undefined ? '—' : Number(n).toLocaleString('en-IN'))
export const pct = (n) => (n === null || n === undefined ? '—' : `${n}%`)
export const errText = (e, fallback = 'Something went wrong.') => e?.response?.data?.message || e?.message || fallback

/** Quick date ranges for the filter bar (delivery dates, both ends included). */
export const QUICK_RANGES = [
  { key: '7d', label: 'Last 7 days', range: () => [subDays(new Date(), 6), new Date()] },
  { key: '30d', label: 'Last 30 days', range: () => [subDays(new Date(), 29), new Date()] },
  { key: 'tm', label: 'This month', range: () => [startOfMonth(new Date()), new Date()] },
  { key: 'lm', label: 'Last month', range: () => [startOfMonth(subMonths(new Date(), 1)), endOfMonth(subMonths(new Date(), 1))] },
]

/** Bucket colour: first bucket calm, last bucket red, the ones between warming up. */
export const bucketTone = (i, count) => {
  if (count <= 1) return 'error'
  const tones = ['success', 'warning', 'orange', 'error']
  return tones[Math.round((i / (count - 1)) * (tones.length - 1))]
}

export const TONE = {
  success: { fg: '#1B7F4B', bg: '#EEF8F2', border: '#BFE3CD' },
  warning: { fg: '#9A6700', bg: '#FFF8E6', border: '#F5DFA3' },
  orange: { fg: '#B54708', bg: '#FFF2E8', border: '#F9CDA6' },
  error: { fg: '#B42318', bg: '#FFF1F0', border: '#F7C1BC' },
  neutral: { fg: '#475467', bg: '#F5F7FA', border: '#E4E7EC' },
}

export const SOURCE_LABEL = { API: 'API', MANUAL: 'Manual' }

export const TIMELINESS = {
  ON_TIME: { label: 'On time', tone: 'success' },
  LATE: { label: 'Late', tone: 'error' },
  NOT_TRACKED: { label: 'Not tracked', tone: 'neutral' },
}

/** Query params for the backend from the page filters; blanks dropped. */
export const toParams = (o) => Object.fromEntries(Object.entries(o || {}).filter(([, v]) => v !== undefined && v !== null && v !== ''))

/** Builds a CSV in the browser and saves it (for tables already on screen, e.g. the scorecard). */
export const saveCsv = (filename, header, rows) => {
  const cell = (v) => {
    const s = v === null || v === undefined ? '' : String(v)
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
  }
  const text = [header, ...rows].map((r) => r.map(cell).join(',')).join('\n')
  const href = window.URL.createObjectURL(new Blob([`﻿${text}`], { type: 'text/csv;charset=utf-8' }))
  const a = document.createElement('a')
  a.href = href
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => window.URL.revokeObjectURL(href), 1000)
}

/** Hours as people say them: "9.1 h" under two days, "20.1 days" above. */
export const fmtHours = (h) => (h === null || h === undefined ? '—' : h < 48 ? `${h} h` : `${Math.round((h / 24) * 10) / 10} days`)
